export type Locale = 'en' | 'nb' | 'es';
export type LocaleInput = string | null | undefined | Locale;

interface SeparatorFormattingRules {
    decimalSeparator: string;
    thousandsSeparator: string;
}

interface SpacingFormattingRules {
    weightUnitSpacing: string;
    percentageSpacing: string;
    temperatureSpacing: string;
    tiltSpacing: string;
    universalUnitSpacing: string;
}

export type LocaleFormattingRules = SeparatorFormattingRules & SpacingFormattingRules;

const LOCALE_RULES: Record<Locale, LocaleFormattingRules> = {
    en: {
        decimalSeparator: '.', // "25.5"
        thousandsSeparator: ',', // "1,234.56"
        weightUnitSpacing: ' ', // "1,234.56 kg"
        percentageSpacing: '', // "25.5%"
        temperatureSpacing: '', // "22.5°C"
        tiltSpacing: '', // "22.5°"
        universalUnitSpacing: ' ', // "25.5 m"
    },
    nb: {
        decimalSeparator: ',', // "25,5"
        thousandsSeparator: ' ', // "1 234,56"
        weightUnitSpacing: ' ', // "1 234,56 kg"
        percentageSpacing: ' ', // "25,5 %"
        temperatureSpacing: ' ', // "22,5 °C"
        tiltSpacing: '', // "22,5°"
        universalUnitSpacing: ' ', // "25,5 m"
    },
    es: {
        decimalSeparator: ',', // "25,5"
        thousandsSeparator: '.', // "1.234,56"
        weightUnitSpacing: ' ', // "1.234,56 kg"
        percentageSpacing: ' ', // "25,5 %"
        temperatureSpacing: ' ', // "22,5 °C"
        tiltSpacing: '', // "22,5°"
        universalUnitSpacing: ' ', // "25,5 m"
    },
};

export const DEFAULT_LOCALE: Locale = 'en';

/** A number that can be shown: neither missing nor NaN. */
export function isDisplayableNumber(value: unknown): value is number {
    return typeof value === 'number' && !Number.isNaN(value);
}

/**
 * A finite number or a numeric string. The unit formatters take loosely typed API data, where a
 * figure can arrive as "12.5". `Number('')` is 0 and `Number('Infinity')` is not NaN, so a blank
 * string and infinity are ruled out here rather than shown as "0" or "Infinity".
 */
export function isNumeric(value: unknown): boolean {
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'string') return value.trim() !== '' && Number.isFinite(Number(value));
    return false;
}

export function parseLocale(input: LocaleInput): Locale {
    if (!input) return DEFAULT_LOCALE;
    const lower = input.toLowerCase();

    if (['en', 'en-us', 'en-gb'].includes(lower)) return 'en';
    if (['es', 'es-es', 'es-mx'].includes(lower)) return 'es';
    if (['nb', 'no', 'nb-no', 'nn'].includes(lower)) return 'nb';

    const base = lower.split('-')[0];
    if (base === 'en') return 'en';
    if (base === 'es') return 'es';
    if (base === 'nb' || base === 'no' || base === 'nn') return 'nb';

    return DEFAULT_LOCALE;
}

/** The separators and unit spacing of a locale; anything unrecognised reads as English. */
export function getLocaleRules(locale: LocaleInput): LocaleFormattingRules {
    return LOCALE_RULES[parseLocale(locale)];
}
